/* The app-wide TIME CURSOR — one clock for every prediction on the chart.
 *
 * Live, the tide icons and the current arrows read the same 1 Hz clock as the
 * rest of the boat. Scrubbed, they freeze together at whatever instant the
 * timeline is parked on — "what will Dodd Narrows be doing at 14:00" becomes
 * one gesture, and the tide and the stream can never disagree because both
 * read the same frozen number. M8.1/M8.2.
 *
 * It is a question about the chart, like the measure tool, so it is not
 * persisted. It lands on the first-paint chunk (ChartPage reads it there), so
 * it stays small on purpose and the scrubber is a lazy chunk.
 *
 * The clamp is the load-bearing bit: a prediction past the end of the shipped
 * year is a fabrication, and the layers drop out there, so the cursor may
 * never pass the data. The covered range is reported in by the host as each
 * dataset lazily loads (see currents.range / tideStations.range) — this store
 * never imports them, so it cannot pull them onto first paint.
 *
 * ═══ THE LIVE CLOCK IS INJECTED, AND THAT IS THE ONLY CHANGE ON THE WAY HERE ═══
 *
 * This lived in the dash and read `sk.now` — the SignalK server's clock, which
 * on a boat is the right answer and not the same as the browser's. A hosted
 * chart has no server to ask, so "what time is it" became the one thing the
 * host supplies. Everything else — the clamp, the range, the scrub span, the
 * label — is identical, which is the point: parking the cursor has to mean the
 * same thing on the boat and on the web, or the same chart would answer the
 * same question two ways.
 */

/** The shipped datasets cover a year of 2026 and no further. 31 Dec 2026 23:59:59Z. */
const DATA_END = 1798761599000;

/** Clamp `t` to the covered range. Pure, so tests can feed any range without
 * loading a year of data. */
export function clampToRange(t: number, min: number, max: number): number {
  return t < min ? min : t > max ? max : t;
}

/**
 * What "live" means. The boat replaces it with the SignalK server's clock.
 *
 * A FUNCTION and not a value, obviously — but also deliberately NOT reactive:
 * it is read from `now`, which is itself read inside effects and templates that
 * already track the parked instant and the 1 Hz tick. Making the clock a
 * `$state` would add a second, redundant dependency to every one of them.
 */
let liveNow: () => number = () => Date.now();

/** Call once at boot, before the map is built. */
export function setLiveClock(fn: () => number): void {
  liveNow = fn;
}

export class ViewTime {
  /** The parked instant, or null while live. */
  #t = $state<number | null>(null);
  /** The covered [min, max] once any dataset has reported in. */
  #range = $state<[number, number] | null>(null);

  /** The instant every prediction layer draws. A plain getter — reading it in
   * a template or effect tracks the underlying `$state`, so it re-renders on
   * the clock tick or on a scrub without the weight of a `$derived`. */
  get now(): number {
    return this.#t ?? liveNow();
  }

  get isLive(): boolean {
    return this.#t === null;
  }

  /**
   * The actual clock, whatever the cursor is doing.
   *
   * ═══ WHY A SEPARATE GETTER, WHEN `now` LOOKS LIKE THE SAME THING ═══
   *
   * They are the same thing right up until somebody scrubs, and then they are
   * the two ends of the only measurement a scrubber makes: "how far from now is
   * this". A control that computes that from `now` is subtracting a number from
   * itself, and it reads "· now" at every position on the slider.
   *
   * Measured exactly that way — the cursor parked six hours ahead, every tide
   * symbol on the chart correctly showing the tide six hours ahead, and the
   * label underneath insisting it was now.
   *
   * A slider's SPAN has the same problem: anchored to `now` it moves with the
   * thumb, so a drag shifts the scale under the finger and can never come back
   * to where it started. Both belong on this.
   */
  get live(): number {
    return liveNow();
  }

  /** The covered range, for the timeline. Null until a dataset has loaded. */
  get range(): [number, number] | null {
    return this.#range;
  }

  /** Widen the covered range as each dataset reports in, capped at the end of
   * the shipped year so a scrub can never pass it. */
  setRange(min: number, max: number): void {
    const cap = Math.min(max, DATA_END);
    const r = this.#range;
    this.#range = [r ? Math.min(r[0], min) : min, r ? Math.max(r[1], cap) : cap];
  }

  /** Park the cursor at `t`, clamped to the covered range. */
  scrub(t: number): void {
    const r = this.#range;
    if (r) t = clampToRange(t, r[0], r[1]);
    this.#t = t;
  }

  /** Back to live — the cursor is the 1 Hz clock again. */
  reset(): void {
    this.#t = null;
  }
}

export const viewTime = new ViewTime();

/* ═══ THE ONE SCRUBBER'S SPAN ═══
 *
 * Backwards as well as forwards. The old scrubber lived in the tide/current
 * popups with `min = sk.now`, so "what was the tide doing when we anchored"
 * could not be asked at all — only "what will it do later".
 *
 * -12 h answers that without pretending to be a logbook, and +48 h is as far
 * ahead as the owner allowed the stream to be predicted. Both ends sit
 * comfortably inside what every scrubbed layer can actually serve: the tide and
 * tidal-stream predictions ship a whole year, and the sea-current model
 * publishes a rolling window roughly 145 h back and 34 h forward — so nothing
 * silently blanks at either end. */
export const SCRUB_BACK_MS = 12 * 3_600_000;
export const SCRUB_FWD_MS = 48 * 3_600_000;
/**
 * ONE MINUTE.
 *
 * It was ten, on the reasoning that a tide does not move faster than that and a
 * wet finger wants a coarse target. That reasoning was about the LABEL, and it
 * was wrong about the chart: with the sea-current field interpolated
 * continuously in time and rendered per frame, a ten-minute step was the only
 * thing left making a drag feel notched — the field visibly jumped from one
 * detent to the next while the streaks in between were smooth.
 *
 * A minute is still coarse enough to land somewhere repeatable (the slider is
 * ~300 px for 60 h, so a pixel is a quarter of an hour — you cannot land on a
 * minute by accident anyway) and fine enough that the field flows. The ±1 h
 * buttons are what a finger uses when it wants an exact hour.
 */
export const SCRUB_STEP_MS = 60_000;

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * "TUE 14:20 · +2h" — the parked instant, and how far it is from now.
 * "· now" is reserved for an exactly-zero rounded offset: a parked cursor
 * even a minute away must say how far away it actually is.
 *
 * Lifted out of ObjectSheet, which had grown its own copy, so the bar and the
 * sheet cannot describe the same cursor two different ways.
 */
export function scrubLabel(t: number, now: number): string {
  const d = new Date(t);
  const when = `${DAYS[d.getDay()]!} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const mins = Math.round((t - now) / 60_000);
  if (mins === 0) return `${when} · now`;
  const h = mins / 60;
  const rel = Math.abs(h) < 1 ? `${mins > 0 ? '+' : ''}${mins}m` : `${h > 0 ? '+' : ''}${h.toFixed(h % 1 ? 1 : 0)}h`;
  return `${when} · ${rel}`;
}
